import * as React from "react";
import { List, ListItemIcon, ListItemText, Divider, ClickAwayListener } from "@mui/material";
import LogoutRounded from "@mui/icons-material/LogoutRounded";
import ExitToAppRounded from "@mui/icons-material/ExitToAppRounded";
import {
  AdminPieMenuStyledPaper,
  AdminPieMenuWrapper,
  CornerTrigger,
  HighlightSpan,
  HoverChip,
  MenuHeaderContainer,
  MenuHeaderText,
  PersonIcon,
  StyledListItemButton,
  StyledPopper,
  TriggerIconWrapper,
} from "../../Styles/GlobalStyles";

type Props = {
  onSignOutAdmin?: () => void;
  onExitAccount?: () => void;
  size?: number;
  title?: string;
};

export default function AdminPieMenu({ onSignOutAdmin, onExitAccount, size = 65, title }: Props) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement | null>(null);
  const [impersonatedEmail, setImpersonatedEmail] = React.useState<string | null>(null);
  const handleClickToggle = () => setOpen((v) => !v);
  const handleClose = () => setOpen(false);
  const handleSignOutAdmin = React.useCallback(() => {
    onSignOutAdmin?.();
    setOpen(false);
  }, [onSignOutAdmin]);

  const handleExitAccount = React.useCallback(() => {
    onExitAccount?.();
    setOpen(false);
  }, [onExitAccount]);

  React.useEffect(() => {
    const email = localStorage.getItem("impersonatedEmail");
    setImpersonatedEmail(email);
  }, []);

  const isImpersonationPath =
    window.location.pathname?.includes("/impersonation") ||
    window.location.pathname?.includes("/adminscreen");

  const menuItems = [
    {
      key: "exit",
      label: "Exit Impersonation",
      icon: <ExitToAppRounded fontSize="small" />,
      onClick: handleExitAccount,
      visible: !isImpersonationPath,
    },
    {
      key: "signOut",
      label: "Sign Out Admin",
      icon: <LogoutRounded fontSize="small" />,
      onClick: handleSignOutAdmin,
      visible: true,
    },
  ];

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <AdminPieMenuWrapper>
        <CornerTrigger
          ref={anchorRef}
          aria-label="Account"
          aria-haspopup="menu"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClickToggle}
          size={size}>
          <TriggerIconWrapper size={size}>
            <PersonIcon size={size} />
          </TriggerIconWrapper>

          <HoverChip className="hoverChip" label={title} size="small" />
        </CornerTrigger>

        <StyledPopper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          disablePortal
          modifiers={[
            { name: "offset", options: { offset: [-12, 12] } },
            { name: "preventOverflow", options: { padding: 8 } },
          ]}>
          <AdminPieMenuStyledPaper elevation={3} role="menu">
            {impersonatedEmail && (
              <MenuHeaderContainer>
                <MenuHeaderText variant="subtitle2">
                  Logged in as : <HighlightSpan>{impersonatedEmail}</HighlightSpan>
                </MenuHeaderText>
              </MenuHeaderContainer>
            )}
            <Divider />

            <List dense disablePadding>
              {menuItems.map(
                (item) =>
                  item.visible && (
                    <StyledListItemButton key={item.key} onClick={item.onClick}>
                      <ListItemIcon className="menuIcon">{item.icon}</ListItemIcon>
                      <ListItemText primary={item.label} />
                    </StyledListItemButton>
                  )
              )}
              <Divider />
            </List>
          </AdminPieMenuStyledPaper>
        </StyledPopper>
      </AdminPieMenuWrapper>
    </ClickAwayListener>
  );
}
